import { supabase } from './client';

const DEFAULT_BUCKET = 'quotation-images';

// Adicionar logging de errros para debug
const logError = (context: string, error: any, details?: any) => {
  console.error(`🚨 Storage Error [${context}]:`, error);
  if (details) {
    console.error('📋 Details:', details);
  }
};

export const storageService = {
  /**
   * Inicializar buckets necessários para o armazenamento
   * Esta função deve ser executada no início da aplicação 
   * para garantir que os buckets existam
   */
  initStorage: async () => {
    // Bucket já existe, então apenas retorne true
    return true;
    
    /* Código original comentado
    try {
      // Verificar se o bucket de imagens de cotação existe e criar se necessário
      const { data: buckets, error: getBucketsError } = await supabase
        .storage
        .listBuckets();
      
      if (getBucketsError) {
        throw getBucketsError;
      }
      
      // Se o bucket padrão não existir, tenta criá-lo
      if (!buckets.find((b: { name: string }) => b.name === DEFAULT_BUCKET)) {
        const { error: createBucketError } = await supabase
          .storage
          .createBucket(DEFAULT_BUCKET, {
            public: true,  // Bucket público para permitir acesso às imagens
          });
        
        if (createBucketError) {
          throw createBucketError;
        }
      }
      
      return true;
    } catch (error) {
      throw error;
    }
    */
  },
  
  /**
   * Fazer upload de um arquivo para o Supabase Storage
   * @param file - Arquivo a ser enviado
   * @param folder - Pasta dentro do bucket (opcional)
   * @param bucket - Nome do bucket (padrão: DEFAULT_BUCKET)
   * @returns URL pública do arquivo enviado
   */
  uploadFile: async (file: File, folder = '', bucket = DEFAULT_BUCKET): Promise<string> => {
    // Forçar o uso do bucket correto
    bucket = 'quotation-images';
    
    try {
      // Validar o arquivo
      if (!file || file.size === 0) {
        throw new Error('Arquivo inválido ou vazio');
      }

      // Criar caminho do arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      const filePath = folder ? `${folder}/${fileName}` : fileName;
      
      console.log(`📤 Iniciando upload: ${filePath}`);
      
      // Fazer upload
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        logError('uploadFile - Upload failed', error, { filePath, fileSize: file.size, fileType: file.type });
        throw error;
      }

      if (!data?.path) {
        logError('uploadFile - No path returned', 'Upload response missing path', { data });
        throw new Error('Upload falhou: resposta inválida do servidor');
      }
      
      // Obter URL pública
      const { data: { publicUrl } } = supabase
        .storage
        .from(bucket)
        .getPublicUrl(data.path);

      if (!publicUrl) {
        logError('uploadFile - No public URL generated', 'Failed to generate public URL', { path: data.path });
        throw new Error('Falha ao gerar URL pública');
      }

      console.log(`✅ Upload concluído: ${publicUrl}`);
      return publicUrl;
    } catch (error) {
      logError('uploadFile - General error', error, { fileName: file.name, folder, bucket });
      throw error;
    }
  },
  
  /**
   * Fazer upload de uma imagem a partir de uma URL externa
   * @param url - URL da imagem a ser baixada e enviada
   * @param folder - Pasta dentro do bucket (opcional)
   * @param bucket - Nome do bucket (padrão: DEFAULT_BUCKET)
   * @returns URL pública do arquivo enviado
   */
  uploadImageFromUrl: async (url: string, folder = '', bucket = DEFAULT_BUCKET): Promise<string> => {
    // Forçar o uso do bucket correto
    bucket = 'quotation-images';
    
    try {
      // Verificar se a URL é válida
      if (!url.startsWith('http')) {
        // Se for base64 ou outro formato que não seja URL, retorna a mesma string
        if (url.length > 200) {
          return url; // Provavelmente é uma imagem base64
        }
        throw new Error("URL inválida");
      }
      
      console.log(`📥 Baixando imagem: ${url}`);
      
      // Usar o proxy para evitar problemas de CORS
      // Essa é uma opção temporária; o ideal seria ter um serviço de proxy próprio
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
      
      // Baixar imagem usando o proxy
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error(`Erro ao baixar imagem: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Imagem baixada está vazia');
      }
      
      // Extrair extensão da URL ou usar .jpg como padrão
      const urlParts = url.split('/');
      const originalFileName = urlParts[urlParts.length - 1].split('?')[0];
      const fileExt = originalFileName.includes('.') 
        ? originalFileName.split('.').pop() 
        : 'jpg';
      
      // Criar nome de arquivo único
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      
      // Criar objeto File a partir do Blob
      const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
      
      // Fazer upload usando a função uploadFile
      return await storageService.uploadFile(file, folder, bucket);
    } catch (error) {
      logError('uploadImageFromUrl - Primary proxy failed', error, { url });
      
      // Em caso de erro no proxy, podemos tentar um proxy alternativo
      try {
        console.log(`🔄 Tentando proxy alternativo para: ${url}`);
        
        const altProxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
        
        const response = await fetch(altProxyUrl);
        if (!response.ok) {
          throw new Error(`Erro ao baixar imagem com proxy alternativo: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        if (blob.size === 0) {
          throw new Error('Imagem baixada pelo proxy alternativo está vazia');
        }
        
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.jpg`;
        const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        
        return await storageService.uploadFile(file, folder, bucket);
      } catch (secondError) {
        logError('uploadImageFromUrl - Alternative proxy failed', secondError, { url });
        // ❌ MUDANÇA CRÍTICA: Em vez de retornar a URL original, vamos propagar o erro
        throw new Error(`Falha ao fazer upload da imagem de ${url}: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      }
    }
  },
  
  /**
   * Excluir um arquivo do Supabase Storage
   * @param url - URL pública do arquivo a ser excluído ou caminho relativo
   * @param bucket - Nome do bucket (padrão: DEFAULT_BUCKET)
   * @returns true se a exclusão foi bem-sucedida
   */
  deleteFile: async (url: string, bucket = DEFAULT_BUCKET): Promise<boolean> => {
    try {
      // Extrair caminho do arquivo da URL
      let filePath: string;
      
      if (url.includes(bucket)) {
        // É uma URL pública completa do Supabase
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        // Encontrar o índice do bucket no caminho
        const bucketIndex = pathParts.findIndex(part => part === bucket);
        // Obter o caminho relativo (tudo após o bucket)
        filePath = pathParts.slice(bucketIndex + 1).join('/');
      } else {
        // É um caminho relativo
        filePath = url;
      }
      
      // Remover o arquivo
      const { error } = await supabase
        .storage
        .from(bucket)
        .remove([filePath]);
      
      if (error) {
        logError('deleteFile', error, { url, filePath });
        throw error;
      }
      
      return true;
    } catch (error) {
      logError('deleteFile - General error', error, { url });
      return false;
    }
  },
  
  /**
   * Migrar imagens externas para o Supabase Storage
   * @param urls - Array de URLs de imagens
   * @param folder - Pasta dentro do bucket (opcional)
   * @param bucket - Nome do bucket (padrão: DEFAULT_BUCKET)
   * @returns Array com as novas URLs do Supabase
   */
  migrateImages: async (urls: string[], folder = '', bucket = DEFAULT_BUCKET): Promise<string[]> => {
    // Forçar o uso do bucket correto
    bucket = 'quotation-images';
    
    if (!urls || urls.length === 0) return [];
    
    console.log(`📦 Iniciando migração de ${urls.length} imagens`);
    
    const newUrls: string[] = [];
    const errors: Array<{url: string, error: string}> = [];
    
    // Processar cada URL em sequência para evitar sobrecarga
    for (const url of urls) {
      try {
        // Verificar se a URL já é do Supabase Storage
        if (url.includes(bucket)) {
          newUrls.push(url);
          continue;
        }
        
        // Fazer upload da imagem
        const newUrl = await storageService.uploadImageFromUrl(url, folder, bucket);
        newUrls.push(newUrl);
      } catch (error) {
        // ❌ MUDANÇA CRÍTICA: Em vez de manter a URL original, vamos rastrear o erro
        const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
        errors.push({ url, error: errorMsg });
        logError('migrateImages - Failed to migrate image', error, { url });
        
        // Manter a URL original por compatibilidade, mas marcar o erro
        newUrls.push(url);
      }
    }
    
    // Se houver erros, mostrar um resumo
    if (errors.length > 0) {
      console.error(`⚠️ Migração completada com ${errors.length} erro(s) de ${urls.length} imagens:`);
      errors.forEach(({url, error}) => {
        console.error(`  - ${url}: ${error}`);
      });
    } else {
      console.log(`✅ Migração completada com sucesso: ${newUrls.length} imagens`);
    }
    
    return newUrls;
  },

  // Limpar cabeçalho para simplificar
  clearPromptsCache: () => {},

  /**
   * Upload strategy that only uploads files for direct user actions
   * External URLs are preserved as-is during duplication and other operations
   * @param source - The source: 'user-upload' for direct uploads, 'external' for URLs, 'duplication' for page copies
   * @param file - File object for user uploads
   * @param url - URL string for external sources
   * @param folder - Storage folder
   */
  smartUpload: async (
    source: 'user-upload' | 'external' | 'duplication',
    data: File | string,
    folder = ''
  ): Promise<string> => {
    console.log(`🤖 Smart Upload Strategy - Source: ${source}`);

    switch (source) {
      case 'user-upload':
        // Only upload to Supabase for direct user uploads
        if (!(data instanceof File)) {
          throw new Error('User uploads must be File objects');
        }
        console.log('📤 User upload detected - uploading to Supabase');
        return await storageService.uploadFile(data, folder);

      case 'external':
      case 'duplication':
        // Keep external URLs as-is, don't upload to Supabase
        if (typeof data !== 'string') {
          throw new Error('External sources must be URL strings');
        }
        console.log('🔗 External URL detected - preserving original URL');
        return data;

      default:
        throw new Error(`Unknown upload source: ${source}`);
    }
  },
};

export default storageService; 